import { useState, useEffect, useMemo } from 'react'
import { useParams, Link, useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getProject } from '../api/projects'
import { getElectricalPanels, type ElectricalPanel } from '../api/electricalPanels'
import { getSpecificationItem } from '../api/specification'
import { getDeviceInstanceById, updateDeviceInstance, getDeviceInstanceSchemas, getDeviceInstanceConnections } from '../api/deviceInstances'
import { getDeviceContacts } from '../api/deviceContacts'
import type { DeviceInstance } from '../types'
import PostsSection from './Posts/PostsSection'
import DeviceInstanceUniversalDiagram from './DeviceInstanceUniversalDiagram'
import { useAuth } from '../AuthContext'
import { PermissionRow } from '../api/admin'
import { apiClient } from '../api/client'

export default function DeviceInstanceViewPage() {
  const { projectId, panelId, instanceId } = useParams<{ projectId: string; panelId?: string; instanceId: string }>()
  const [searchParams] = useSearchParams()
  const specificationId = searchParams.get('spec') ? Number(searchParams.get('spec')) : undefined
  const [showEditForm, setShowEditForm] = useState(false)
  const [mouseDownTarget, setMouseDownTarget] = useState<EventTarget | null>(null)
  const { user: currentUser } = useAuth()
  
  const queryClient = useQueryClient()

  // Загрузка прав пользователя на проект
  const { data: projectAccessData } = useQuery({
    queryKey: ['my-project-access', currentUser?.id],
    queryFn: async () => {
      if (!currentUser?.id) return { access: [] }
      const response = await apiClient.get('/api/auth/my-project-access')
      return response.data as { access: Array<{ project_id: number; role_in_project: string }> }
    },
    enabled: !!currentUser?.id && currentUser.role !== 'system_admin',
  })

  // Загрузка всех permissions для текущего пользователя и проекта
  const { data: permissionsData } = useQuery({
    queryKey: ['my-permissions', currentUser?.id, projectId],
    queryFn: async () => {
      if (!currentUser?.id || !projectId) return { permissions: [] }
      const response = await apiClient.get('/api/auth/my-permissions', {
        params: { project_id: Number(projectId) },
      })
      return response.data as { permissions: PermissionRow[] }
    },
    enabled: !!currentUser?.id && !!projectId && currentUser.role !== 'system_admin',
  })

  const allPermissions: PermissionRow[] = permissionsData?.permissions ?? []

  // Проверка, может ли пользователь редактировать (не только просмотр)
  const canEdit = useMemo(() => {
    if (currentUser?.role === 'system_admin') return true
    if (!projectId || !currentUser?.id) return false

    // Проверяем права на страницу экземпляров устройств
    const pagePermission = allPermissions.find(
      (p) =>
        p.project_id === Number(projectId) &&
        p.resource_type === 'page:device-instances' &&
        p.resource_id === null &&
        p.permission !== 'deny' &&
        (p.permission === 'edit' || p.permission === 'manage')
    )

    if (pagePermission) return true

    // Проверяем наследование от роли проекта
    const access = projectAccessData?.access?.find(
      (a) => a.project_id === Number(projectId),
    )
    if (access && access.role_in_project) {
      return ['editor', 'project_admin'].includes(access.role_in_project)
    }

    return false
  }, [currentUser, projectId, allPermissions, projectAccessData])

  const { data: project, isLoading: projectLoading } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => getProject(Number(projectId)),
    enabled: !!projectId,
  })

  const { data: instance, isLoading: instanceLoading, error: instanceError } = useQuery({
    queryKey: ['deviceInstance', instanceId],
    queryFn: () => getDeviceInstanceById(Number(instanceId!)),
    enabled: !!instanceId,
  })

  const { data: specification } = useQuery({
    queryKey: ['specification', instance?.specification_id],
    queryFn: () => getSpecificationItem(Number(instance!.specification_id)),
    enabled: !!instance?.specification_id,
  })

  const { data: contacts = [], isLoading: contactsLoading } = useQuery({
    queryKey: ['deviceContacts', instance?.specification_id],
    queryFn: () => getDeviceContacts(Number(instance!.specification_id)),
    enabled: !!instance?.specification_id,
  })

  const { data: instanceSchemas = [], isLoading: instanceSchemasLoading } = useQuery({
    queryKey: ['deviceInstanceSchemas', instanceId],
    queryFn: () => getDeviceInstanceSchemas(Number(instanceId!)),
    enabled: !!instanceId,
  })

  // Load electrical panels for edit form
  const { data: electricalPanels = [], isLoading: electricalPanelsLoading } = useQuery({
    queryKey: ['electricalPanels', projectId],
    queryFn: () => getElectricalPanels(Number(projectId!)),
    enabled: !!projectId && showEditForm,
  })

  // Update instance mutation
  const updateInstanceMutation = useMutation({
    mutationFn: async (data: { id: number; updates: Partial<DeviceInstance> }) => {
      await updateDeviceInstance(data.id, data.updates)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deviceInstance', instanceId] })
      queryClient.invalidateQueries({ queryKey: ['deviceInstances', projectId] })
      setShowEditForm(false)
    },
    onError: (error) => {
      console.error('❌ Failed to update instance:', error)
      alert(`Ошибка обновления экземпляра: ${error instanceof Error ? error.message : 'Неизвестная ошибка'}`)
    }
  })

  // Close modal on ESC key
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowEditForm(false)
      }
    }

    document.addEventListener('keydown', handleEscKey)
    return () => {
      document.removeEventListener('keydown', handleEscKey)
    }
  }, [])

  const handleEditInstance = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!instance) return
    
    const formData = new FormData(e.currentTarget)
    
    const electricalPanelId = formData.get('electrical_panel_id') as string
    
    if (!electricalPanelId) {
      alert('Пожалуйста, выберите электрический щит')
      return
    }
    
    const updates = {
      position_label: (formData.get('position_label') as string || '').trim() || null,
      description: (formData.get('description') as string || '').trim() || null,
      knx_address: (formData.get('knx_address') as string || '').trim() || null,
      electrical_panel_id: parseInt(electricalPanelId),
      is_reserve: formData.get('is_reserve') ? 1 : 0
    }

    updateInstanceMutation.mutate({ id: instance.id, updates })
  }

  const closeEditModal = () => {
    setShowEditForm(false)
    setMouseDownTarget(null)
  }

  if (projectLoading || instanceLoading) {
    return <div className="text-center py-8">Загрузка...</div>
  }

  if (!project) {
    return <div className="text-center py-8 text-red-600">Проект не найден</div>
  }

  if (instanceError || !instance) {
    return (
      <div className="text-center py-8 text-red-600">
        Экземпляр не найден
        <div className="mt-4">
          <Link 
            to={specificationId 
              ? `/projects/${projectId}/device-instances?spec=${specificationId}`
              : panelId 
                ? `/projects/${projectId}/panels/${panelId}/device-instances`
                : `/projects/${projectId}/device-instances`
            }
            className="text-blue-600 hover:text-blue-800"
          >
            ← Вернуться к списку экземпляров
          </Link>
        </div>
      </div>
    )
  }

  // Сортируем контакты по номеру
  const sortedContacts = [...contacts].sort((a, b) => {
    const numA = parseInt(String(a.contact_number).replace(/\D/g, '')) || 0
    const numB = parseInt(String(b.contact_number).replace(/\D/g, '')) || 0
    return numA - numB
  })

  // Цветовые коды
  const CONTACT_COLORS: Record<string, { label: string; bg: string; text: string }> = {
    WH: { label: 'Белый', bg: 'bg-white', text: 'text-gray-900' },
    BU: { label: 'Синий', bg: 'bg-blue-500', text: 'text-white' },
    BN: { label: 'Коричневый', bg: 'bg-amber-800', text: 'text-white' },
    GY: { label: 'Серый', bg: 'bg-gray-500', text: 'text-white' },
    GN: { label: 'Зеленый', bg: 'bg-green-500', text: 'text-white' },
    YE: { label: 'Желтый', bg: 'bg-yellow-400', text: 'text-gray-900' },
    RD: { label: 'Красный', bg: 'bg-red-500', text: 'text-white' },
    OG: { label: 'Оранжевый', bg: 'bg-orange-500', text: 'text-white' },
    PK: { label: 'Розовый', bg: 'bg-pink-400', text: 'text-gray-900' },
    VT: { label: 'Фиолетовый', bg: 'bg-purple-500', text: 'text-white' },
    GNYE: { label: 'Желто-зеленый', bg: 'bg-green-400', text: 'text-gray-900' },
    BK: { label: 'Черный', bg: 'bg-gray-900', text: 'text-white' },
  }

  const getColorInfo = (colorCode: string) => {
    return CONTACT_COLORS[colorCode] || { label: colorCode, bg: 'bg-gray-300', text: 'text-gray-900' }
  }

  const getPositionLabel = (position: string | null) => {
    if (!position) return '—'
    const labels: Record<string, string> = {
      top: '⬆️ Верхний',
      bottom: '⬇️ Нижний',
      left: '⬅️ Левый',
      right: '➡️ Правый'
    }
    return labels[position] || position
  }

  const getBackUrl = () => {
    if (specificationId) {
      return `/projects/${projectId}/device-instances?spec=${specificationId}`
    }
    if (panelId) {
      return `/projects/${projectId}/panels/${panelId}/device-instances`
    }
    return `/projects/${projectId}/device-instances`
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <Link to={`/projects/${projectId}`} className="text-blue-600 hover:text-blue-800 mb-2 inline-block">
            ← Назад к {project.name}
          </Link>
          <Link 
            to={getBackUrl()}
            className="text-blue-600 hover:text-blue-800 mb-2 ml-4 inline-block"
          >
            ← К списку экземпляров
          </Link>
          <h1 className="text-3xl font-bold text-gray-900">
            <span>{instance.position_label || `${instance.spec_name || 'Экземпляр'} #${instance.instance_number}`}</span>
            <span className="text-lg font-normal text-gray-500 ml-1">— экземпляр устройства</span>
          </h1>
          <p className="text-gray-700 mt-0.5">
            <span className="font-medium">{instance.spec_name || 'Неизвестное устройство'}</span>
            <span className="text-gray-500 text-base ml-1">— устройство</span>
          </p>
        </div>
        {canEdit && (
          <button
            onClick={() => setShowEditForm(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            ✏️ Редактировать
          </button>
        )}
      </div>

      {/* Instance Information */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Информация об экземпляре</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className="text-sm text-gray-500">ID экземпляра</div>
            <div className="text-lg font-medium text-gray-900">#{instance.id}</div>
          </div>
          <div>
            <div className="text-sm text-gray-500">Позиционное обозначение</div>
            <div className="text-lg font-medium text-gray-900">
              {instance.position_label || `${instance.spec_name || '—'} #${instance.instance_number}`}
            </div>
          </div>
          {instance.spec_name && (
            <div>
              <div className="text-sm text-gray-500">Родительский элемент</div>
              <Link 
                to={`/projects/${projectId}/devices/${instance.specification_id}`}
                className="text-lg font-medium text-blue-600 hover:text-blue-800 hover:underline"
              >
                {instance.spec_name}
              </Link>
            </div>
          )}
          {instance.panel_proj_id && (
            <div>
              <div className="text-sm text-gray-500">Электрический щит</div>
              <div className="text-lg font-medium text-gray-900">{instance.panel_proj_id}</div>
            </div>
          )}
          {instance.device_type_name && (
            <div>
              <div className="text-sm text-gray-500">Тип устройства</div>
              <div className="text-lg font-medium text-gray-900">{instance.device_type_name}</div>
            </div>
          )}
          {instance.instance_number && (
            <div>
              <div className="text-sm text-gray-500">Номер экземпляра</div>
              <div className="text-lg font-medium text-gray-900">#{instance.instance_number}</div>
            </div>
          )}
          {instance.knx_address && (
            <div>
              <div className="text-sm text-gray-500">KNX адрес</div>
              <div className="text-lg font-medium text-gray-900">{instance.knx_address}</div>
            </div>
          )}
          {instance.is_reserve === 1 && (
            <div>
              <div className="text-sm text-gray-500">Статус</div>
              <div className="text-lg font-medium text-orange-600">Резервный</div>
            </div>
          )}
          {instance.description && (
            <div className="md:col-span-2">
              <div className="text-sm text-gray-500">Описание</div>
              <div className="text-lg font-medium text-gray-900">{instance.description}</div>
            </div>
          )}
        </div>
      </div>

      {/* Схемы и щиты, где находится экземпляр */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">На каких схемах и в каких щитах находится этот экземпляр</h2>
        {instanceSchemasLoading ? (
          <p className="text-gray-500">Загрузка...</p>
        ) : instanceSchemas.length === 0 ? (
          <p className="text-gray-500">Экземпляр не размещён ни на одной схеме.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Щит</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Схема</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase"></th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {instanceSchemas.map((row) => (
                  <tr key={row.schema_id}>
                    <td className="px-4 py-2 text-gray-700">
                      {row.panel_proj_id || row.panel_name || '—'}
                    </td>
                    <td className="px-4 py-2 text-gray-900">
                      {row.schema_name}
                    </td>
                    <td className="px-4 py-2">
                      <Link
                        to={`/projects/${projectId}/schemas/${row.schema_id}?focus=${instance.id}`}
                        className="text-blue-600 hover:text-blue-800 hover:underline text-sm font-medium"
                      >
                        Показать на схеме
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Specification Information */}
      {specification && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Информация о спецификации</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="text-sm text-gray-500">ID спецификации</div>
              <div className="text-lg font-medium text-gray-900">#{specification.id}</div>
            </div>
            {specification.manufacturer && (
              <div>
                <div className="text-sm text-gray-500">Производитель</div>
                <div className="text-lg font-medium text-gray-900">{specification.manufacturer}</div>
              </div>
            )}
            {specification.model && (
              <div>
                <div className="text-sm text-gray-500">Модель</div>
                <div className="text-lg font-medium text-gray-900">{specification.model}</div>
              </div>
            )}
            {specification.device_type_name && (
              <div>
                <div className="text-sm text-gray-500">Тип устройства</div>
                <div className="text-lg font-medium text-gray-900">{specification.device_type_name}</div>
              </div>
            )}
            {specification.power_consumption && (
              <div>
                <div className="text-sm text-gray-500">Потребляемая мощность</div>
                <div className="text-lg font-medium text-gray-900">{specification.power_consumption} Вт</div>
              </div>
            )}
            {specification.unit_price && (
              <div>
                <div className="text-sm text-gray-500">Цена за единицу</div>
                <div className="text-lg font-medium text-gray-900">{specification.unit_price} ₽</div>
              </div>
            )}
            {specification.width && specification.height && specification.depth && (
              <>
                <div>
                  <div className="text-sm text-gray-500">Ширина</div>
                  <div className="text-lg font-medium text-gray-900">{specification.width} мм</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">Высота</div>
                  <div className="text-lg font-medium text-gray-900">{specification.height} мм</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">Глубина</div>
                  <div className="text-lg font-medium text-gray-900">{specification.depth} мм</div>
                </div>
              </>
            )}
            {specification.description && (
              <div className="md:col-span-2">
                <div className="text-sm text-gray-500">Описание</div>
                <div className="text-lg font-medium text-gray-900">{specification.description}</div>
              </div>
            )}
            {specification.documentation_url && (
              <div>
                <div className="text-sm text-gray-500">Документация</div>
                <a 
                  href={specification.documentation_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 underline"
                >
                  Открыть документацию
                </a>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Contacts Information */}
      {specification && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Контакты устройства</h2>
          {contactsLoading ? (
            <div className="text-center py-4 text-gray-500">Загрузка контактов...</div>
          ) : sortedContacts.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      № контакта
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Маркировка
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Позиция
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Цвет
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Визуально
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {sortedContacts.map((contact) => {
                    const colorCode = contact.color_code || ''
                    const colorInfo = getColorInfo(colorCode)
                    return (
                      <tr key={contact.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {contact.contact_number}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {contact.contact_label || '—'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {getPositionLabel(contact.contact_position)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {colorCode ? `${colorCode} - ${colorInfo.label}` : '—'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {colorCode ? (
                            <div 
                              className={`w-8 h-8 rounded border-2 border-gray-300 ${colorInfo.bg} ${colorInfo.text} flex items-center justify-center text-xs font-bold`}
                              title={`${colorCode} - ${colorInfo.label}`}
                            >
                              {colorCode}
                            </div>
                          ) : (
                            <div className="w-8 h-8 rounded border-2 border-gray-300 bg-gray-100 flex items-center justify-center text-xs text-gray-400">
                              —
                            </div>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              У устройства нет настроенных контактов
            </div>
          )}
        </div>
      )}

      {/* Edit Form Modal */}
      {showEditForm && instance && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              setMouseDownTarget(e.target)
            }
          }}
          onMouseUp={(e) => {
            if (e.target === e.currentTarget && mouseDownTarget === e.target) {
              closeEditModal()
            }
            setMouseDownTarget(null)
          }}
        >
          <div 
            className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-full overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold mb-4">Редактировать экземпляр устройства <span className="instance-id">#{instance.id}</span></h3>
            
            {/* Parent specification info - compact */}
            <div className="mb-3 px-3 py-2 bg-gray-50 border border-gray-200 rounded text-xs text-gray-600">
              <span className="font-medium">Спецификация:</span> ID {instance.specification_id} • {instance.spec_name || '—'}
              {instance.manufacturer && <span> • {instance.manufacturer}</span>}
              {instance.model && <span> • {instance.model}</span>}
            </div>

            <form onSubmit={handleEditInstance} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Позиционное обозначение</label>
                <input
                  type="text"
                  name="position_label"
                  defaultValue={instance.position_label || ''}
                  placeholder="напр., ДП-14.2-1, LD-1-1, УР-2-4"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Электрический щит</label>
                {electricalPanelsLoading ? (
                  <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500">
                    Загрузка щитов...
                  </div>
                ) : (
                  <select
                    name="electrical_panel_id"
                    required
                    defaultValue={instance.electrical_panel_id || ''}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="">Выберите электрический щит...</option>
                    {(electricalPanels as ElectricalPanel[]).map((panel: ElectricalPanel) => (
                      <option key={panel.id} value={panel.id}>
                        {panel.name} ({panel.proj_id})
                      </option>
                    ))}
                  </select>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Описание</label>
                <textarea
                  name="description"
                  defaultValue={instance.description || ''}
                  placeholder="Дополнительное описание экземпляра..."
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>

              {/* KNX Address field - show for KNX devices (device_type_id = 14) and Actuators (device_type_id = 5) */}
              {(instance.device_type_id === 14 || instance.device_type_id === 5) && (
                <div>
                  <label className="block text-sm font-medium mb-1">KNX адрес</label>
                  <input
                    type="text"
                    name="knx_address"
                    defaultValue={instance.knx_address || ''}
                    placeholder="напр., 1.1.1"
                    pattern="^\d+\.\d+\.\d+$"
                    title="Формат: 1.255.255"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                  <p className="text-xs text-gray-500 mt-1">Формат: 1.255.255</p>
                </div>
              )}

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_reserve_device_instance_view"
                  name="is_reserve"
                  defaultChecked={Boolean(instance?.is_reserve)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="is_reserve_device_instance_view" className="ml-2 text-sm font-medium text-gray-700">
                  Резервный
                </label>
              </div>

              <div className="flex space-x-2 pt-4">
                <button
                  type="submit"
                  disabled={updateInstanceMutation.isPending}
                  className="flex-1 bg-blue-600 text-white py-2 rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {updateInstanceMutation.isPending ? 'Сохранение...' : 'Сохранить'}
                </button>
                <button
                  type="button"
                  onClick={closeEditModal}
                  className="flex-1 bg-gray-600 text-white py-2 rounded-md hover:bg-gray-700"
                >
                  Отмена
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <DeviceInstanceUniversalDiagram instanceId={Number(instanceId!)} projectId={Number(projectId!)} />

      <PostsSection
        entityType="device_instance"
        entityId={Number(instanceId!)}
        canEdit={canEdit}
      />
    </div>
  )
}

interface ConnectionDiagramProps {
  instanceId: number;
  projectId: number;
}

export function DeviceInstanceConnectionDiagrams({ instanceId, projectId }: ConnectionDiagramProps) {
  const [activeSchemaId, setActiveSchemaId] = useState<number | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['deviceInstanceConnections', instanceId],
    queryFn: () => getDeviceInstanceConnections(instanceId),
    enabled: !!instanceId,
  });

  const CONTACT_COLORS: Record<string, { label: string; bg: string; text: string }> = {
    WH: { label: 'Белый', bg: 'bg-white', text: 'text-gray-900' },
    BU: { label: 'Синий', bg: 'bg-blue-500', text: 'text-white' },
    BN: { label: 'Коричневый', bg: 'bg-amber-800', text: 'text-white' },
    GY: { label: 'Серый', bg: 'bg-gray-500', text: 'text-white' },
    GN: { label: 'Зеленый', bg: 'bg-green-500', text: 'text-white' },
    YE: { label: 'Желтый', bg: 'bg-yellow-400', text: 'text-gray-900' },
    RD: { label: 'Красный', bg: 'bg-red-500', text: 'text-white' },
    OG: { label: 'Оранжевый', bg: 'bg-orange-500', text: 'text-white' },
    PK: { label: 'Розовый', bg: 'bg-pink-400', text: 'text-gray-900' },
    VT: { label: 'Фиолетовый', bg: 'bg-purple-500', text: 'text-white' },
    GNYE: { label: 'Желто-зеленый', bg: 'bg-green-400', text: 'text-gray-900' },
    BK: { label: 'Черный', bg: 'bg-gray-900', text: 'text-white' },
  };

  const getColorInfo = (colorCode: string) => {
    return CONTACT_COLORS[colorCode] || { label: colorCode, bg: 'bg-gray-300', text: 'text-gray-900' };
  };

  const activeSchema = useMemo(() => {
    if (!data?.schemas || data.schemas.length === 0) return null;
    if (activeSchemaId === null) {
      return data.schemas[0];
    }
    return data.schemas.find(s => s.schema_id === activeSchemaId) || data.schemas[0];
  }, [data, activeSchemaId]);

  useEffect(() => {
    if (data?.schemas && data.schemas.length > 0 && activeSchemaId === null) {
      setActiveSchemaId(data.schemas[0].schema_id);
    }
  }, [data, activeSchemaId]);

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6 animate-pulse">
        <div className="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
        <div className="h-10 bg-gray-100 rounded mb-6"></div>
        <div className="h-48 bg-gray-50 rounded"></div>
      </div>
    );
  }

  if (error || !data?.schemas || data.schemas.length === 0) {
    return null;
  }

  // Trace and sort upward nodes top-down (highest ancestor first)
  const sortedUpNodes = activeSchema ? (() => {
    const up = activeSchema.nodes.filter(n => n.role === 'up');
    if (up.length === 0) return [];
    
    const parentMap = new Map<number, number>();
    for (const c of activeSchema.connections) {
      parentMap.set(Number(c.to_instance_id), Number(c.from_instance_id));
    }
    
    const path: any[] = [];
    let curr = instanceId;
    const visited = new Set<number>();
    
    while (parentMap.has(curr)) {
      curr = parentMap.get(curr)!;
      if (visited.has(curr)) break;
      visited.add(curr);
      const foundNode = up.find(n => Number(n.id) === curr);
      if (foundNode) {
        path.push(foundNode);
      } else {
        break;
      }
    }
    
    return path.reverse();
  })() : [];

  const targetNode = activeSchema?.nodes.find(n => n.role === 'target');
  const neighborNodes = activeSchema?.nodes.filter(n => n.role === 'neighbor') || [];
  const downNodes = activeSchema?.nodes.filter(n => n.role === 'down') || [];

  const terminals = downNodes.filter(n => n.device_type_code === 'terminal');
  const cables = downNodes.filter(n => n.device_type_code === 'cable');
  const endDevices = downNodes.filter(n => n.device_type_code !== 'terminal' && n.device_type_code !== 'cable');

  return (
    <div className="bg-white rounded-lg shadow-md p-6 space-y-6">
      <div className="flex justify-between items-center border-b border-gray-100 pb-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Схемы соединений контактов</h2>
          <p className="text-sm text-gray-500 mt-0.5">Взаимосвязи устройства на различных схемах электрического щита</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2">
        {data.schemas.map(s => (
          <button
            key={s.schema_id}
            onClick={() => setActiveSchemaId(s.schema_id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeSchema?.schema_id === s.schema_id
                ? 'bg-blue-600 text-white shadow-sm'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            📋 {s.schema_name}
          </button>
        ))}
      </div>

      {activeSchema && targetNode && (
        <div className="space-y-8 pt-4">
          {/* Visual Diagram Area */}
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 flex flex-col items-center overflow-x-auto min-w-full">
            
            {/* 1. UPWARD NODES (BREAKERS) */}
            {sortedUpNodes.map((node) => (
              <div key={node.id} className="flex flex-col items-center w-full">
                <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-3 shadow-sm max-w-[240px] w-full text-center transition-all hover:border-amber-400 hover:shadow-md">
                  <span className="text-xxs font-bold text-amber-800 uppercase tracking-wider block mb-1">
                    ⚡ Питание / Защита
                  </span>
                  <Link
                    to={`/projects/${projectId}/device-instances/${node.id}`}
                    className="text-sm font-bold text-blue-600 hover:underline block"
                  >
                    {node.position_label || `${node.spec_name} #${node.instance_number}`}
                  </Link>
                  <span className="text-xs text-gray-500 block truncate" title={node.spec_name}>
                    {node.spec_name}
                  </span>
                  
                  {/* Contacts */}
                  {node.contacts.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1 justify-center border-t border-amber-200/50 pt-1.5">
                      {node.contacts.map((c: any) => {
                        const colorInfo = getColorInfo(c.color_code || '');
                        return (
                          <span
                            key={c.id}
                            className="px-1.5 py-0.5 bg-white border border-amber-200 rounded text-xxs font-mono flex items-center gap-1 font-bold text-gray-700"
                            title={c.contact_label || undefined}
                          >
                            {c.color_code && <span className={`w-1.5 h-1.5 rounded-full ${colorInfo.bg}`} />}
                            {c.contact_number}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
                
                {/* Arrow down */}
                <div className="w-0.5 h-6 bg-gray-300 flex items-center justify-center my-1">
                  <span className="text-gray-400 text-xs mt-3">↓</span>
                </div>
              </div>
            ))}

            {/* 2. CURRENT LEVEL (TARGET & NEIGHBORS) */}
            <div className="flex flex-col md:flex-row items-center justify-center gap-8 w-full max-w-4xl py-2">
              
              {/* Neighbors (horizontal communication, etc.) */}
              {neighborNodes.length > 0 && (
                <div className="flex flex-col gap-3 max-w-[240px] w-full md:order-1">
                  <span className="text-xxs font-bold text-gray-400 uppercase tracking-wider text-center block mb-1">
                    🔄 Связанные устройства (Соседи)
                  </span>
                  {neighborNodes.map((node) => (
                    <div key={node.id} className="bg-purple-50 border-2 border-purple-200 rounded-lg p-3 shadow-xs transition-all hover:border-purple-300 hover:shadow-sm">
                      <span className="text-xxs font-bold text-purple-700 uppercase tracking-wider block mb-1 text-center">
                        Соседнее устройство
                      </span>
                      <Link
                        to={`/projects/${projectId}/device-instances/${node.id}`}
                        className="text-sm font-bold text-blue-600 hover:underline block text-center"
                      >
                        {node.position_label || `${node.spec_name} #${node.instance_number}`}
                      </Link>
                      <span className="text-xs text-gray-500 block text-center truncate" title={node.spec_name}>
                        {node.spec_name}
                      </span>
                      
                      {/* Contacts */}
                      {node.contacts.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1 justify-center border-t border-purple-100 pt-1.5">
                          {node.contacts.map((c: any) => {
                            const colorInfo = getColorInfo(c.color_code || '');
                            return (
                              <span
                                key={c.id}
                                className="px-1.5 py-0.5 bg-white border border-purple-100 rounded text-xxs font-mono flex items-center gap-1"
                                title={c.contact_label || undefined}
                              >
                                {c.color_code && <span className={`w-1.5 h-1.5 rounded-full ${colorInfo.bg}`} />}
                                <span className="font-bold text-gray-700">{c.contact_number}</span>
                              </span>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Target Device (prominent center card) */}
              <div className="bg-blue-50 border-3 border-blue-500 rounded-xl p-5 shadow-lg max-w-[340px] w-full text-center relative ring-4 ring-blue-100 ring-offset-2 md:order-2">
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-600 text-white text-xxs font-bold px-3 py-1 rounded-full uppercase tracking-wider shadow-sm">
                  Текущее устройство
                </span>
                
                <div className="pt-2">
                  <span className="text-base font-bold text-gray-900 block">
                    {targetNode.position_label || `${targetNode.spec_name} #${targetNode.instance_number}`}
                  </span>
                  <span className="text-xs text-gray-500 block truncate mt-0.5" title={targetNode.spec_name}>
                    {targetNode.spec_name}
                  </span>
                </div>

                {/* Contacts grid */}
                {targetNode.contacts.length > 0 && (
                  <div className="mt-4 border-t border-blue-200/60 pt-3">
                    <span className="text-xxs font-bold text-blue-700 uppercase tracking-wider block mb-2">Контакты на схеме</span>
                    <div className="grid grid-cols-2 gap-2 text-left">
                      {targetNode.contacts.map((c: any) => {
                        const colorInfo = getColorInfo(c.color_code || '');
                        return (
                          <div
                            key={c.id}
                            className="flex items-center gap-1.5 p-1.5 bg-white rounded border border-blue-100 text-xs shadow-xxs"
                          >
                            <span
                              className={`w-3 h-3 rounded-full border border-gray-300 flex-shrink-0 ${colorInfo.bg}`}
                              title={colorInfo.label}
                            />
                            <span className="font-mono font-bold text-gray-700">{c.contact_number}</span>
                            {c.contact_label && (
                              <span
                                className="text-gray-500 truncate max-w-[80px] text-xxs font-medium"
                                title={c.contact_label}
                              >
                                {c.contact_label}
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* 3. DOWNWARD FLOW */}
            
            {/* Terminals Level */}
            {terminals.length > 0 && (
              <div className="flex flex-col items-center w-full">
                {/* Arrow down */}
                <div className="w-0.5 h-6 bg-gray-300 flex items-center justify-center my-1">
                  <span className="text-gray-400 text-xs mt-3">↓</span>
                </div>
                
                <span className="text-xxs font-bold text-gray-400 uppercase tracking-wider mb-2 block">
                  📍 Клеммы распределения
                </span>
                <div className="flex flex-wrap gap-3 justify-center max-w-2xl w-full">
                  {terminals.map((node) => (
                    <div
                      key={node.id}
                      className="bg-teal-50 border-2 border-teal-300 rounded-lg p-3 shadow-sm min-w-[160px] text-center transition-all hover:border-teal-400 hover:shadow-md"
                    >
                      <span className="text-xxs font-bold text-teal-800 uppercase tracking-wider block mb-1">
                        Клемма
                      </span>
                      <Link
                        to={`/projects/${projectId}/device-instances/${node.id}`}
                        className="text-sm font-bold text-blue-600 hover:underline block"
                      >
                        {node.position_label || `${node.spec_name} #${node.instance_number}`}
                      </Link>
                      
                      {/* Contacts */}
                      {node.contacts.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1 justify-center border-t border-teal-200/40 pt-1.5">
                          {node.contacts.map((c: any) => {
                            const colorInfo = getColorInfo(c.color_code || '');
                            return (
                              <span
                                key={c.id}
                                className="px-1.5 py-0.5 bg-white border border-teal-200 rounded text-xxs font-mono flex items-center gap-1 font-bold text-gray-700"
                                title={c.contact_label || undefined}
                              >
                                {c.color_code && <span className={`w-1.5 h-1.5 rounded-full ${colorInfo.bg}`} />}
                                {c.contact_number}
                              </span>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Cables Level */}
            {cables.length > 0 && (
              <div className="flex flex-col items-center w-full">
                {/* Arrow down */}
                <div className="w-0.5 h-6 bg-gray-300 flex items-center justify-center my-1">
                  <span className="text-gray-400 text-xs mt-3">↓</span>
                </div>
                
                <span className="text-xxs font-bold text-gray-400 uppercase tracking-wider mb-2 block">
                  🔌 Кабели линии
                </span>
                <div className="flex flex-col gap-3 justify-center max-w-xl w-full">
                  {cables.map((node) => (
                    <div
                      key={node.id}
                      className="bg-gray-100 border-2 border-gray-400 rounded-xl py-2.5 px-5 shadow-sm text-center relative transition-all hover:border-gray-500 hover:shadow-md"
                    >
                      <span className="text-xxs font-bold text-gray-700 uppercase tracking-wider block mb-0.5">
                        Кабельная линия
                      </span>
                      <Link
                        to={`/projects/${projectId}/device-instances/${node.id}`}
                        className="text-sm font-bold text-blue-600 hover:underline block"
                      >
                        {node.position_label || `${node.spec_name} #${node.instance_number}`}
                      </Link>
                      <span className="text-xxs text-gray-500 font-mono block mt-0.5">
                        {node.spec_name}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* End Devices Level */}
            {endDevices.length > 0 && (
              <div className="flex flex-col items-center w-full">
                {/* Arrow down */}
                <div className="w-0.5 h-6 bg-gray-300 flex items-center justify-center my-1">
                  <span className="text-gray-400 text-xs mt-3">↓</span>
                </div>
                
                <span className="text-xxs font-bold text-gray-400 uppercase tracking-wider mb-2 block">
                  🎯 Конечные потребители / Устройства
                </span>
                <div className="flex flex-wrap gap-3 justify-center max-w-2xl w-full">
                  {endDevices.map((node) => (
                    <div
                      key={node.id}
                      className="bg-emerald-50 border-2 border-emerald-300 rounded-lg p-3 shadow-sm min-w-[160px] text-center transition-all hover:border-emerald-400 hover:shadow-md"
                    >
                      <span className="text-xxs font-bold text-emerald-800 uppercase tracking-wider block mb-1">
                        Потребитель / Нагрузка
                      </span>
                      <Link
                        to={`/projects/${projectId}/device-instances/${node.id}`}
                        className="text-sm font-bold text-blue-600 hover:underline block"
                      >
                        {node.position_label || `${node.spec_name} #${node.instance_number}`}
                      </Link>
                      <span className="text-xs text-gray-500 block truncate" title={node.spec_name}>
                        {node.spec_name}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

          </div>

          {/* Connections List / Table view */}
          <div className="border border-gray-100 rounded-xl overflow-hidden shadow-xs">
            <div className="bg-gray-50 px-4 py-3 border-b border-gray-100">
              <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
                🔗 Детальная таблица связей контактов
              </h3>
            </div>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50/50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xxs font-bold text-gray-500 uppercase tracking-wider">Откуда</th>
                    <th className="px-4 py-2 text-center text-xxs font-bold text-gray-500 uppercase tracking-wider">Направление</th>
                    <th className="px-4 py-2 text-left text-xxs font-bold text-gray-500 uppercase tracking-wider">Куда</th>
                    <th className="px-4 py-2 text-left text-xxs font-bold text-gray-500 uppercase tracking-wider">Кабель / Провод</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100 text-xs">
                  {activeSchema.connections.map((c) => {
                    const fromNode = activeSchema.nodes.find(n => n.id === c.from_instance_id);
                    const toNode = activeSchema.nodes.find(n => n.id === c.to_instance_id);
                    
                    if (!fromNode || !toNode) return null;
                    
                    const isFromTarget = fromNode.id === instanceId;
                    const isToTarget = toNode.id === instanceId;
                    const highlightRow = isFromTarget || isToTarget;
                    
                    let wireProps: any = {};
                    if (c.wire_properties) {
                      try {
                        wireProps = typeof c.wire_properties === 'string' ? JSON.parse(c.wire_properties) : c.wire_properties;
                        if (typeof wireProps === 'string') {
                          wireProps = JSON.parse(wireProps);
                        }
                      } catch (e) {}
                    }
                    
                    return (
                      <tr key={c.id} className={`hover:bg-gray-50 ${highlightRow ? 'bg-blue-50/20 font-medium' : ''}`}>
                        <td className="px-4 py-2.5">
                          <Link to={`/projects/${projectId}/device-instances/${fromNode.id}`} className="text-blue-600 hover:underline font-semibold">
                            {fromNode.position_label || `${fromNode.spec_name} #${fromNode.instance_number}`}
                          </Link>
                        </td>
                        <td className="px-4 py-2.5 text-center text-gray-400">
                          →
                        </td>
                        <td className="px-4 py-2.5">
                          <Link to={`/projects/${projectId}/device-instances/${toNode.id}`} className="text-blue-600 hover:underline font-semibold">
                            {toNode.position_label || `${toNode.spec_name} #${toNode.instance_number}`}
                          </Link>
                        </td>
                        <td className="px-4 py-2.5 text-gray-600 font-mono text-xxs">
                          <span className="px-1.5 py-0.5 bg-gray-100 rounded text-gray-700 uppercase tracking-wider mr-1">
                            {c.connection_type || 'wire'}
                          </span>
                          {wireProps.color ? `Цвет: ${wireProps.color}` : ''}
                          {wireProps.cross_section ? ` (${wireProps.cross_section} мм²)` : ''}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
